import React, { useState, useEffect } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TextInput,
    TouchableOpacity,
} from 'react-native';
import { createPortal } from 'react-dom';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import DataTable from "react-data-table-component";
import { sensorColumns } from './common';
import Data from './Data';
import { getStorage, setStorage } from '../storage';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};
type Theme = typeof DARK;

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

// 스크롤 가능한 영역 안에서 발생한 이벤트인지 판별 (모달 내부 드롭다운 등)
const isInScrollable = (target: any) => {
    let n: any = target;
    while (n && n !== document.body && n.nodeType === 1) {
        const ov = getComputedStyle(n).overflowY;
        if ((ov === 'auto' || ov === 'scroll') && n.scrollHeight > n.clientHeight + 1) return true;
        n = n.parentElement;
    }
    return false;
};

const blockDrag = (el: any) => {
    if (!el || el._bd) return;
    el._bd = true;
    const pd = (e: Event) => { if (e.type === 'wheel' && isInScrollable(e.target)) return; e.preventDefault(); };
    el.addEventListener('mousedown', pd);
    el.addEventListener('touchstart', pd, { passive: false });
    el.addEventListener('wheel', pd, { passive: false });
    el.addEventListener('dragstart', pd);
};
const blockContentDrag = (el: any) => {
    if (!el || el._bcd) return;
    el._bcd = true;
    const pd = (e: Event) => { if (isInScrollable(e.target)) return; e.preventDefault(); e.stopPropagation(); };
    el.addEventListener('wheel', pd, { passive: false });
    el.addEventListener('touchmove', pd, { passive: false });
};

// ─── 메인 컴포넌트 ────────────────────────────────────
const headers = ['이름', '전화번호', '담당 구역', '근무 표시'];

export default function MemberDetail({ navigation, route }: any) {
    const [__p] = useState(() => {
        if (typeof window === 'undefined') return route?.params || {};
        try {
            const id = route?.params?.id;
            const key = `_navParams_MemberDetail_${id ?? ''}`;
            const saved = window.localStorage.getItem(key);
            if (saved) return JSON.parse(saved);
        } catch {}
        return route?.params || {};
    });
    const data = __p?.data;
    const isFocused = useIsFocused();
    const [sensor, setSensor] = useState<any>([]);
    const [focus, setFocus] = useState(false);
    const [phone, setPhone] = useState(data?.PHONE);
    const [admin, setAdmin] = useState('1');
    const [dark, setDark] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);

    useEffect(() => {
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
        getStorage('AutoLogin').then((v: any) => { if (v?.admin !== undefined) setAdmin(v.admin); });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    useEffect(() => {
        navigation.setOptions({
            headerStyle: { backgroundColor: '#304156', elevation: 0, shadowOpacity: 0, borderBottomWidth: 0 },
            headerTintColor: '#fff',
        });
    }, []);

    useEffect(() => {
        if (!deleteModal) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const prevent = (e: Event) => { if (isInScrollable(e.target)) return; e.preventDefault(); e.stopPropagation(); };
        document.addEventListener('wheel', prevent, { passive: false } as any);
        document.addEventListener('touchmove', prevent, { passive: false } as any);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener('wheel', prevent);
            document.removeEventListener('touchmove', prevent);
        };
    }, [deleteModal]);

    const tableStyles = {
        headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: T.border, borderBottomWidth: 1, minHeight: 40 } },
        headCells:  { style: { color: T.textSub, fontSize: 12, fontWeight: '600' as any, paddingLeft: 12, paddingRight: 12 } },
        rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: T.border, borderBottomWidth: 1, minHeight: 44, '&:hover': { backgroundColor: T.cardInner } } },
        cells:      { style: { color: T.text, fontSize: 13, paddingLeft: 12, paddingRight: 12 } },
        pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: 44 },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
        noData:     { style: { backgroundColor: T.card, color: T.muted, padding: 30 } },
    };

    useEffect(() => {
        if (!isFocused) return;
        Data.fetchData('sensor/getSensorListByCaregiver', {
            CAREGIVER_ID: data?.ID
        }, (result: any, count: any) => {
            if (count > 0) {
                setSensor(result);
            } else {
                setSensor([]);
            }
        }, true);
    }, [isFocused]);

    const onFocus = () => setFocus(!focus);

    const onSave = () => {
        Data.fetchData('pushApp/updateCaregiverPhone', {
            PHONE: phone,
            OLD_PHONE: data?.PHONE,
            NAME: data?.NAME
        }, (result: any, count: any) => {
            if (count > 0) {
                setFocus(false);
                alert('수정되었습니다.');
            } else {
                alert('저장에 실패하였습니다.');
            }
        }, true);
    };

    const onDelete = () => {
        Data.fetchData('sensor/deleteCaregiver', {
            DATA: [{ "ID": data?.ID }]
        }, (result: any, count: any) => {
            if (count > 0) {
                alert('삭제되었습니다.');
                navigation.goBack();
            } else {
                alert('삭제에 실패하였습니다.');
            }
        }, true);
    };

    const isAdmin = admin === '0';

    return (
        <View style={[styles.container, { backgroundColor: T.bg }]}>
            {/* ─── 보호사 정보 카드 ─── */}
            <View style={[styles.card, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                <View style={styles.cardHeader}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <Text style={{ fontSize: 18, fontWeight: '700', color: T.text }}>보호사 정보</Text>
                        <View style={{ backgroundColor: (data?.WORKING === 1 ? S.ok : S.err) + '20', paddingHorizontal: 10, paddingVertical: 3, borderRadius: 10, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: data?.WORKING === 1 ? S.ok : S.err }} />
                            <Text style={{ fontSize: 12, fontWeight: '600', color: data?.WORKING === 1 ? S.ok : S.err }}>
                                {data?.WORKING === 1 ? '출근' : '퇴근'}
                            </Text>
                        </View>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        {focus ? (
                            <>
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.ok }]} onPress={onSave}>
                                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#fff' }}>저장</Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: T.muted }]} onPress={onFocus}>
                                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#fff' }}>취소</Text>
                                </TouchableOpacity>
                            </>
                        ) : (
                            <>
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.accent }]} onPress={onFocus}>
                                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#fff' }}>수정</Text>
                                </TouchableOpacity>
                                {isAdmin && (
                                    <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.err }]} onPress={() => setDeleteModal(true)}>
                                        <Text style={{ fontSize: 13, fontWeight: '600', color: '#fff' }}>삭제</Text>
                                    </TouchableOpacity>
                                )}
                            </>
                        )}
                        <TouchableOpacity
                            style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                            onPress={() => { const next = !dark; setDark(next); setStorage('darkMode', next); }}
                        >
                            <Ionicons name={dark ? 'sunny' : 'moon'} size={16} color={dark ? '#FBBF24' : '#6366F1'} />
                        </TouchableOpacity>
                    </View>
                </View>

                {/* 정보 테이블 */}
                <View style={[styles.infoTable, { borderColor: T.border }]}>
                    <View style={[styles.infoRow, { backgroundColor: T.cardInner, borderBottomWidth: 1, borderBottomColor: T.border }]}>
                        {headers.map((h, i) => (
                            <View key={h} style={[styles.infoCell, i < headers.length - 1 && { borderRightWidth: 1, borderRightColor: T.border }]}>
                                <Text style={{ fontSize: 13, fontWeight: '600', color: T.textSub }}>{h}</Text>
                            </View>
                        ))}
                    </View>
                    <View style={styles.infoRow}>
                        <View style={[styles.infoCell, { borderRightWidth: 1, borderRightColor: T.border }]}>
                            <Text style={{ fontSize: 13, color: T.text }}>{data?.NAME}</Text>
                        </View>
                        <View style={[styles.infoCell, { borderRightWidth: 1, borderRightColor: T.border }]}>
                            <TextInput autoCapitalize='none' editable={focus}
                                style={[styles.infoInput, { color: T.text, borderColor: focus ? S.accent : 'transparent', borderWidth: focus ? 1 : 0, backgroundColor: focus ? T.cardInner : 'transparent' }]}
                                value={phone} onChangeText={(text: any) => setPhone(text)} />
                        </View>
                        <View style={[styles.infoCell, { borderRightWidth: 1, borderRightColor: T.border }]}>
                            <Text style={{ fontSize: 13, color: T.text }}>{data?.AREA}</Text>
                        </View>
                        <View style={styles.infoCell}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: data?.WORKING === 1 ? S.ok : S.err }} />
                                <Text style={{ fontSize: 13, color: data?.WORKING === 1 ? S.ok : S.err, fontWeight: '600' }}>
                                    {data?.WORKING === 1 ? '출근' : '퇴근'}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>
            </View>

            {/* ─── 센서 리스트 카드 ─── */}
            <View style={[styles.card, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                <View style={styles.cardHeader}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <Text style={{ fontSize: 18, fontWeight: '700', color: T.text }}>{data?.NAME}님 센서 리스트</Text>
                        {sensor?.length > 0 && (
                            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 }}>
                                <Text style={{ fontSize: 12, fontWeight: '700', color: S.accent }}>{sensor.length}</Text>
                            </View>
                        )}
                    </View>
                </View>
                <DataTable
                    columns={sensorColumns}
                    data={sensor}
                    pagination
                    paginationPerPage={5}
                    paginationRowsPerPageOptions={[5, 10]}
                    customStyles={tableStyles}
                    noDataComponent={
                        <View style={{ padding: 40, alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: 16, color: T.muted }}>연결된 센서가 없습니다.</Text>
                        </View>
                    }
                    onRowClicked={(row: any) => { navigation.navigate('SensorDetail', { data: row }) }}
                />
            </View>

            {/* ─── 삭제 확인 모달 ─── */}
            {deleteModal && createPortal(
                <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
                    <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                        <TouchableOpacity activeOpacity={1} onPress={() => setDeleteModal(false)} style={{ flex: 1 }} />
                    </View>
                    <View ref={blockContentDrag} style={[styles.confirmModal, { backgroundColor: T.card, borderColor: T.border, zIndex: 10 }]}>
                        <Ionicons name="warning-outline" size={36} color={S.err} style={{ marginBottom: 12 }} />
                        <Text style={{ fontSize: 16, fontWeight: '600', color: T.text, textAlign: 'center' }}>
                            해당 보호사를 삭제하시겠습니까?
                        </Text>
                        <Text style={{ fontSize: 12, color: S.err, marginTop: 8 }}>이 작업은 되돌릴 수 없습니다.</Text>
                        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 12, marginTop: 20 }}>
                            <TouchableOpacity style={[styles.modalBtn, { backgroundColor: S.err }]}
                                onPress={() => { setDeleteModal(false); onDelete(); }}>
                                <Text style={styles.modalBtnText}>삭제</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[styles.modalBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                                onPress={() => setDeleteModal(false)}>
                                <Text style={[styles.modalBtnText, { color: T.textSub }]}>취소</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>,
                document.body
            )}
        </View>
    );
}

// ─── 스타일 ─────────────────────────────────────────
const styles = StyleSheet.create({
    container: { flex: 1, padding: 24 },
    card: {
        borderRadius: 16, borderWidth: 0.5, overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 20,
        elevation: 3, paddingTop: 20, paddingHorizontal: 10, marginBottom: 20,
    },
    cardHeader: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        paddingHorizontal: 6, paddingBottom: 14,
    },
    headerBtn: {
        flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, height: 34, borderRadius: 10,
    },
    iconBtn: { width: 34, height: 34, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
    infoTable: { borderWidth: 1, borderRadius: 10, overflow: 'hidden', marginHorizontal: 6, marginBottom: 16 },
    infoRow: { flexDirection: 'row', minHeight: 44 },
    infoCell: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 6 },
    infoInput: { fontSize: 13, textAlign: 'center', width: '90%', height: 30, borderRadius: 6, paddingHorizontal: 6 },
    confirmModal: {
        alignSelf: 'center', width: 340, borderRadius: 16, borderWidth: 1, padding: 30,
        alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.15, shadowRadius: 20, elevation: 10,
    },
    modalBtn: { paddingHorizontal: 24, paddingVertical: 10, borderRadius: 10, alignItems: 'center', minWidth: 90 },
    modalBtnText: { fontSize: 14, fontWeight: '700', color: '#fff' },
});
